import { useCallback, useEffect, useState } from 'react';

const TILT_THRESHOLD = 10;
const UPRIGHT_BETA = 90;
const FLAT_BETA = 0;
const UPRIGHT_GAMMA = -90;
const FLAT_GAMMA = 0;

export const getCameraLevel = ({
  isPortrait,
  tilt,
}: {
  isPortrait: boolean;
  tilt: {
    alpha: number;
    beta: number;
    gamma: number;
  };
}) => {
  if (isPortrait) {
    // Potrait and device is held upright
    const isUpright = tilt.beta >= UPRIGHT_BETA - TILT_THRESHOLD && tilt.beta <= UPRIGHT_BETA + TILT_THRESHOLD;
    if (isUpright) {
      return true;
    }
    // Portrait and device is held flat
    const isFlat = tilt.beta >= FLAT_BETA - TILT_THRESHOLD && tilt.beta <= FLAT_BETA + TILT_THRESHOLD;
    if (isFlat) {
      return true;
    }
    return false;
  }

  // Landscape and device is held flat
  const isFlat = tilt.gamma >= FLAT_GAMMA - TILT_THRESHOLD && tilt.gamma <= FLAT_GAMMA + TILT_THRESHOLD;
  if (isFlat) {
    return true;
  }
  // Landscape and device is held upright
  const isUpright = tilt.gamma >= UPRIGHT_GAMMA - TILT_THRESHOLD && tilt.gamma <= UPRIGHT_GAMMA + TILT_THRESHOLD;
  if (isUpright) {
    return true;
  }
  return false;
};

export const useTilt = () => {
  const [isPortrait, setIsPortrait] = useState(true);

  const [tilt, setTilt] = useState<{
    alpha: number;
    beta: number;
    gamma: number;
  }>({
    alpha: 0,
    beta: 0,
    gamma: 0,
  });

  const handleDeviceOrientation = useCallback((event: DeviceOrientationEvent) => {
    setTilt({
      alpha: event.alpha || 0,
      beta: event.beta || 0,
      gamma: event.gamma || 0,
    });
  }, []);

  const checkOrientation = useCallback(() => {
    const isPortraitMode = window.innerHeight > window.innerWidth;
    setIsPortrait(isPortraitMode);
  }, []);

  useEffect(() => {
    checkOrientation();

    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    if (window.DeviceOrientationEvent) {
      window.addEventListener('deviceorientation', handleDeviceOrientation);
    }

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
      window.removeEventListener('deviceorientation', handleDeviceOrientation);
    };
  }, [handleDeviceOrientation, checkOrientation]);

  return {
    isCameraLevel: getCameraLevel({ isPortrait, tilt }),
    isPortrait,
    tilt,
  };
};
